import { useState } from "react";
import { SolutionDetailDrawer } from "@/components/dashboard/SolutionDetailDrawer";
import { ErrorState } from "@/components/shared/error-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccounts } from "@/hooks/use-accounts";
import type { SignalType } from "@/hooks/use-signals";
import { useSignals } from "@/hooks/use-signals";
import { SignalRow } from "./SignalRow";

const TYPE_OPTIONS: { value: SignalType; label: string }[] = [
  { value: "needs_guidance", label: "Needs Guidance" },
  { value: "guidance_change_pod_change", label: "Pod Change" },
  { value: "intensity_drift", label: "Intensity Drift" },
  { value: "resource_off_pod", label: "Add Resource" },
];

const ALL = "__all__";

export function SignalsTab() {
  const [type, setType] = useState<string>(ALL);
  const [accountId, setAccountId] = useState<string>(ALL);
  const [openSolutionId, setOpenSolutionId] = useState<string | null>(null);

  const { data: accountsPage } = useAccounts({ limit: 100 });
  const accounts = accountsPage?.data ?? [];

  const { data, isLoading, isError, refetch } = useSignals({
    type: type === ALL ? undefined : (type as SignalType),
    accountId: accountId === ALL ? undefined : accountId,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger size="sm" className="w-48">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger size="sm" className="w-56">
            <SelectValue placeholder="All Accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Accounts</SelectItem>
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <ul className="space-y-2" aria-label="Loading signals">
          {[0, 1, 2].map((i) => (
            <li key={i} className="rounded-lg border p-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="mt-2 h-4 w-1/3" />
            </li>
          ))}
        </ul>
      )}

      {!isLoading && isError && (
        <ErrorState message="Failed to load signals." onRetry={() => refetch()} />
      )}

      {!isLoading && !isError && data && data.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No open signals. Solutions are all reconciled against current Guidance.
        </p>
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <ul className="space-y-2">
          {data.map((signal) => (
            <SignalRow key={signal.id} signal={signal} onOpenSolution={setOpenSolutionId} />
          ))}
        </ul>
      )}

      {openSolutionId && (
        <SolutionDetailDrawer id={openSolutionId} onClose={() => setOpenSolutionId(null)} />
      )}
    </div>
  );
}
